import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaUsers, FaGlobe, FaStar } from 'react-icons/fa';
import profilePhoto from '../assets/photo.png';
import cvPdf from '../assets/Mohamed_Amine_Nasfi_CV_EN.pdf';

const ROLE_KEYS = ['fullStack', 'flutter', 'problemSolver'];
const UPWORK_URL = 'https://www.upwork.com/freelancers/~017cad0a2a355873b2?mp_source=share';

const HERO_STAT_CONFIG = [
  { id: 'clients', value: 2, icon: FaUsers, suffix: '+' },
  { id: 'websites', value: 4, icon: FaGlobe, suffix: '+' },
  { id: 'satisfaction', value: 100, icon: FaStar, suffix: '%' },
];

const StatCounter = ({ value, active, suffix = '+' }) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!active) {
      setDisplay(0);
      return undefined;
    }

    let rafId = 0;
    const duration = 1400;
    const startTime = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      setDisplay(Math.round(eased * value));
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [active, value]);

  return (
    <span className="tabular-nums">
      {display}
      <span className="text-cyan-400">{suffix}</span>
    </span>
  );
};

const statsContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.15 },
  },
};

const statItemVariants = {
  hidden: { opacity: 0, y: 14, scale: 0.92 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

const heroBtnBase =
  'hero-cta-btn inline-flex items-center justify-center gap-2 sm:gap-2.5 min-h-[50px] px-3 sm:px-5 md:px-6 py-3 ' +
  'leading-[1.2] overflow-visible font-semibold text-xs sm:text-sm md:text-base ' +
  'rounded-xl transition-all duration-300 whitespace-normal sm:whitespace-nowrap w-full flex-shrink-0 text-center';

const Hero = () => {
  const { t } = useTranslation();
  const [typedRole, setTypedRole] = useState('');
  const [roleIndex, setRoleIndex] = useState(0);
  const timersRef = useRef([]);
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, margin: '-40px' });

  const roles = useMemo(
    () => ROLE_KEYS.map((key) => t(`hero.roles.${key}`)),
    [t],
  );

  const heroStats = useMemo(
    () =>
      HERO_STAT_CONFIG.map((stat) => ({
        ...stat,
        label: t(`hero.stats.${stat.id}`),
      })),
    [t],
  );

  const introHighlights = useMemo(
    () => [
      t('hero.mernStack'),
      t('hero.flutter'),
      t('hero.responsiveWeb'),
      t('hero.crossPlatformMobile'),
    ],
    [t],
  );

  const introContent = useMemo(() => {
    const introText = t('hero.intro', {
      mernStack: '__0__',
      flutter: '__1__',
      responsiveWeb: '__2__',
      crossPlatformMobile: '__3__',
    });
    return introText.split(/(__\d__)/).map((part, index) => {
      const match = part.match(/^__(\d)__$/);
      if (match) {
        return (
          <span key={index} className="text-secondary font-semibold">
            {introHighlights[Number(match[1])]}
          </span>
        );
      }
      return part;
    });
  }, [t, introHighlights]);

  useEffect(() => {
    const currentRole = roles[roleIndex];
    if (!currentRole) return undefined;

    let currentIndex = 0;

    const clearTimers = () => {
      timersRef.current.forEach((id) => {
        clearInterval(id);
        clearTimeout(id);
      });
      timersRef.current = [];
    };

    const typingInterval = setInterval(() => {
      if (currentIndex <= currentRole.length) {
        setTypedRole(currentRole.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        const pauseTimer = setTimeout(() => {
          const deletingInterval = setInterval(() => {
            if (currentIndex > 0) {
              currentIndex--;
              setTypedRole(currentRole.substring(0, currentIndex));
            } else {
              clearInterval(deletingInterval);
              setRoleIndex((prev) => (prev + 1) % roles.length);
            }
          }, 50);
          timersRef.current.push(deletingInterval);
        }, 2000);
        timersRef.current.push(pauseTimer);
      }
    }, 100);

    timersRef.current.push(typingInterval);

    return () => clearTimers();
  }, [roleIndex, roles]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section className="min-h-screen flex items-center justify-center relative bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] overflow-x-clip w-full max-w-full pt-20 sm:pt-24 pb-8" id="home">
      {/* Animated Coding Background */}
      <div className="absolute inset-0 -z-10 pointer-events-none hidden md:flex items-center justify-center w-full max-w-full overflow-hidden">
        <motion.pre
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: [0.12, 0.18, 0.12], y: [40, 0, 40] }}
          transition={{ duration: 12, repeat: Infinity, repeatType: 'reverse' }}
          className="text-xs font-mono text-secondary/70 whitespace-pre leading-snug select-none opacity-20 px-2 sm:px-4 max-w-full sm:max-w-2xl md:max-w-4xl"
          style={{ 
            userSelect: 'none',
            fontSize: 'clamp(0.5rem, 1.5vw, 0.75rem)'
          }}
        >{`
const hero = {
  name: "Mohamed Amine Nasfi",
  role: "Full-Stack Developer",
  stack: ["MongoDB", "Express.js", "React", "Node.js"],
  focus: "Building responsive and user-friendly web applications",
  contact: "#contact",
  projects: "#projects"
};

function buildWeb() {
  return hero.stack.map(tech => "Building with " + tech);
}

console.log("Building amazing web experiences...");
`}</motion.pre>
      </div>
      <div className="flex flex-col md:flex-row-reverse items-center justify-center w-full max-w-full gap-4 sm:gap-6 md:gap-8 lg:gap-12 px-3 sm:px-4 md:px-6 lg:px-8 z-10">
        {/* Professional Photo with Cyan Border - Right Side */}
        <div className="relative flex flex-col items-center justify-center mb-4 md:mb-0 w-full max-w-[280px] sm:max-w-xs md:max-w-sm flex-shrink-0">
          {/* Subtle Background Glow */}
          <motion.div
            className="absolute -z-10 top-1/2 left-1/2 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 rounded-full blur-3xl bg-gradient-to-br from-cyan-500/20 via-teal-500/20 to-emerald-500/10"
            style={{ transform: 'translate(-50%, -50%)' }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.4, 0.6, 0.4] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          
          {/* Photo with Cyan/Teal Glowing Border */}
          <motion.div
            className="relative w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 lg:w-80 lg:h-80 rounded-full p-[3px] bg-gradient-to-br from-cyan-400 via-teal-400 to-cyan-500 shadow-[0_0_40px_rgba(34,211,238,0.6)]"
            animate={{ 
              y: [0, -8, 0],
              boxShadow: [
                '0 0 40px rgba(34,211,238,0.6)',
                '0 0 60px rgba(20,184,166,0.8)',
                '0 0 40px rgba(34,211,238,0.6)'
              ]
            }}
            transition={{ 
              y: { duration: 6, repeat: Infinity, repeatType: 'reverse' },
              boxShadow: { duration: 4, repeat: Infinity, ease: 'easeInOut' }
            }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="w-full h-full rounded-full overflow-hidden bg-white flex items-center justify-center">
              <img
                src={profilePhoto}
                alt={t('hero.photoAlt')}
                className="w-full h-full object-cover rounded-full"
              />
            </div>
          </motion.div>
        </div>
        {/* Hero Text Content - Left Side */}
        <div className="flex flex-col items-center md:items-start justify-center text-center md:text-left w-full max-w-full md:max-w-2xl overflow-visible px-3 sm:px-4">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-3 sm:space-y-4 w-full max-w-full"
          >
            <motion.p 
              variants={itemVariants}
              className="text-secondary text-base sm:text-lg md:text-xl tracking-widest font-semibold mb-1 sm:mb-2 font-mono"
            >
              {t('hero.greeting')}
            </motion.p>
            <motion.h1 
              variants={itemVariants}
              className="text-white text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-extrabold mb-1 sm:mb-2 drop-shadow-lg font-mono leading-tight break-words w-full"
            >
              {t('hero.name')}
            </motion.h1>
            <motion.h2 
              variants={itemVariants}
              className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-bold text-secondary mb-2 sm:mb-4 font-mono flex items-center justify-center md:justify-start gap-2 flex-wrap w-full"
            >
              <span>{typedRole}</span>
              <motion.span
                animate={{ opacity: [1, 0, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
                className="text-secondary"
              >
                |  
              </motion.span>
            </motion.h2>
            <motion.p 
              variants={itemVariants}
              className="max-w-full text-textSecondary text-xs sm:text-sm md:text-base lg:text-lg mb-4 sm:mb-6 leading-relaxed font-mono w-full break-words"
            >
              {introContent}
            </motion.p>
            <motion.div
              variants={itemVariants}
              className="hero-cta-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 w-full max-w-full px-1 sm:px-2 items-stretch overflow-visible"
            >
              {/* Get In Touch */}
              <motion.a
                href="#contact"
                className={`${heroBtnBase} border-2 border-cyan-400/90 text-cyan-400 hover:bg-cyan-400/10 hover:shadow-[0_0_22px_rgba(34,211,238,0.35)] hover:border-cyan-300`}
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 self-center" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="leading-[1.2]">{t('hero.getInTouch')}</span>
              </motion.a>

              {/* Download CV */}
              <motion.a
                href={cvPdf}
                download="Mohamed_Amine_Nasfi_CV_EN.pdf"
                className={`${heroBtnBase} relative bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-bold shadow-lg hover:shadow-[0_0_28px_rgba(34,211,238,0.55)] overflow-visible group sm:col-span-2 lg:col-span-1`}
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-teal-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none" />
                <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/25 to-transparent skew-x-12" />
                </div>
                <motion.svg
                  className="relative z-10 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 self-center"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  animate={{ y: [0, -2, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </motion.svg>
                <span className="relative z-10 leading-[1.2] tracking-wide">{t('hero.downloadCv')}</span>
              </motion.a>

              {/* Hire Me */}
              <motion.a
                href={UPWORK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${heroBtnBase} relative border-2 border-green-400/90 text-green-400 hover:bg-green-400/10 hover:shadow-[0_0_22px_rgba(34,197,94,0.35)] hover:border-green-300 overflow-visible group`}
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
                aria-label={t('hero.hireMeAria')}
              >
                <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-green-400/15 to-transparent skew-x-12" />
                </div>
                <svg className="relative z-10 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 self-center group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18.561 13.158c-1.102 0-2.135-.467-3.074-1.227l.228-1.076.008-.042c.207-1.143.849-3.06 2.839-3.06 1.492 0 2.703 1.212 2.703 2.703-.001 1.489-1.212 2.702-2.704 2.702zm0-8.14c-2.539 0-4.51 1.649-5.31 4.366-1.22-1.834-2.148-4.036-2.687-5.892H7.828v7.112c-.002 1.406-1.141 2.546-2.547 2.548-1.405-.002-2.543-1.143-2.545-2.548V3.492H0v7.112c0 2.914 2.37 5.303 5.281 5.303 2.913 0 5.283-2.389 5.283-5.303v-1.19c.529 1.107 1.182 2.229 1.974 3.221l-1.673 7.873h2.797l1.213-5.71c1.063.679 2.285 1.109 3.686 1.109 3 0 5.439-2.452 5.439-5.45 0-3.002-2.439-5.453-5.439-5.453z" />
                </svg>
                <span className="relative z-10 leading-[1.2]">{t('hero.hireMe')}</span>
              </motion.a>
            </motion.div>

            {/* Freelance Availability Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="flex items-center justify-center md:justify-start gap-2 mt-4 sm:mt-6 w-full px-1"
            >
              <motion.div 
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-green-400 rounded-full shadow-[0_0_10px_rgba(34,197,94,0.6)] flex-shrink-0"
              />
              <span className="text-green-400 font-semibold text-xs sm:text-sm font-mono text-center md:text-left">{t('hero.availableForFreelance')}</span>
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                className="text-green-400"
              >
                
              </motion.span>
            </motion.div>

            {/* Stats — horizontal row below availability */}
            <motion.div
              ref={statsRef}
              variants={statsContainerVariants}
              initial="hidden"
              animate={statsInView ? 'visible' : 'hidden'}
              className="flex flex-row flex-wrap items-center justify-center md:justify-start gap-x-3 gap-y-3 sm:gap-x-5 md:gap-x-6 mt-4 sm:mt-5 w-full px-1 sm:px-2"
            >
              {heroStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <React.Fragment key={stat.id}>
                    {index > 0 && (
                      <motion.div
                        variants={statItemVariants}
                        className="hidden xs:block w-px h-9 sm:h-10 bg-cyan-400/20 shrink-0"
                        aria-hidden="true"
                      />
                    )}
                    <motion.div
                      variants={statItemVariants}
                      whileHover={{ y: -3, scale: 1.03 }}
                      className="flex items-center gap-2 sm:gap-2.5 min-w-0 flex-1 xs:flex-none basis-[30%] xs:basis-auto justify-center xs:justify-start"
                    >
                      <motion.div
                        animate={{ boxShadow: ['0 0 0 rgba(34,211,238,0)', '0 0 12px rgba(34,211,238,0.25)', '0 0 0 rgba(34,211,238,0)'] }}
                        transition={{ duration: 2.5, repeat: Infinity, delay: index * 0.3 }}
                        className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg border border-cyan-400/30 bg-cyan-400/5 text-cyan-400"
                      >
                        <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" aria-hidden="true" />
                      </motion.div>
                      <div className="text-left min-w-0">
                        <p className="text-lg sm:text-xl md:text-2xl font-bold text-white font-mono leading-none">
                          <StatCounter value={stat.value} active={statsInView} suffix={stat.suffix} />
                        </p>
                        <p className="text-[9px] xs:text-[10px] sm:text-xs text-textSecondary font-mono mt-0.5 sm:mt-1 uppercase tracking-wide leading-tight">
                          {stat.label}
                        </p>
                      </div>
                    </motion.div>
                  </React.Fragment>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </div>
      {/* Social Links */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="fixed left-4 xl:left-10 bottom-0 hidden lg:block z-40 pb-[max(1rem,env(safe-area-inset-bottom))]"
      >
        <div className="flex flex-col items-center space-y-6">
          <a href="https://github.com/medaminenasfi" target="_blank" rel="noopener noreferrer"
             aria-label={t('hero.githubAria')}
             className="text-textSecondary hover:text-secondary hover:-translate-y-1 transition-all duration-300 relative z-40">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path fillRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.48 0-.237-.009-.866-.013-1.7-2.782.604-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.464-1.11-1.464-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.137 20.167 22 16.42 22 12c0-5.523-4.477-10-10-10z" clipRule="evenodd" />
            </svg>
          </a>
          <a href="https://www.linkedin.com/in/mohamed-amine-nasfi/" target="_blank" rel="noopener noreferrer"
             aria-label={t('hero.linkedinAria')}
             className="text-textSecondary hover:text-secondary hover:-translate-y-1 transition-all duration-300 relative z-40">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
          <div className="h-24 w-px bg-textSecondary/50"></div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;